import ReneContact from "../components/site/ReneContact";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, useApi, useSeo, fileUrl } from "../lib/api";
import { useSite } from "../lib/store";
import { Loading, Page, PageTitle, Empty, WhatsAppButton, Prose } from "../components/site/Bits";
import { InquiryForm } from "../components/site/Inquiry";

export function About() {
  const { data, loading } = useApi("/public/team");
  useSeo("Über uns", "René und George – wer hinter Kunst und Fund steht.");
  if (loading) return <Loading />;
  return (
    <Page>
      <PageTitle eyebrow="Über uns" title="Kunst in den Alltag bringen." />
      <div className="grid gap-16">{data.map((t, i) => (
        <article key={t.id} className={`grid md:grid-cols-[320px_1fr] gap-8 items-start ${i % 2 ? "md:[&>*:first-child]:order-2 md:grid-cols-[1fr_320px]" : ""}`} data-testid={`team-${i}`}>
          <div className="passe aspect-[4/5] relative">{t.photo ? <img src={fileUrl(t.photo.url)} alt={t.photo.alt || t.name} width={480} height={600} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" /> : <span className="absolute inset-0 grid place-items-center font-display font-black text-7xl text-[#E6DFD5]">{t.name[0]}</span>}</div>
          <div>
            <h2 className="text-3xl sm:text-4xl font-black">{t.artist_slug ? <Link to={`/kuenstler/${t.artist_slug}`} className="hover:text-[#0047FF]">{t.name}</Link> : t.name}</h2>
            <p className="text-[#0047FF] font-semibold mt-1">{t.role_title}</p>
            {t.responsibilities && <p className="text-sm mt-2"><strong>Zuständig für:</strong> {t.responsibilities}</p>}
            <div className="mt-5 text-lg"><Prose text={t.text} /></div>
            {t.artist_slug && <Link to={`/kuenstler/${t.artist_slug}`} className="inline-block mt-5 underline font-semibold">Künstlerprofil & Arbeiten ansehen →</Link>}
            <div className="mt-4 flex flex-wrap gap-4">{(t.links || []).map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="font-semibold link-u">{l.label} ↗</a>)}</div>
            {t.name === "René Ghavami" && <ReneContact />}
          </div>
        </article>))}</div>
    </Page>
  );
}

export function Contact() {
  const site = useSite();
  useSeo("Kontakt");
  return (
    <Page>
      <PageTitle eyebrow="Kontakt" title="Schreib uns." />
      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12">
        <div className="space-y-4 text-sm">
          {site?.contact_email && <p>E-Mail: <a className="underline" href={`mailto:${site.contact_email}`}>{site.contact_email}</a></p>}
          {site?.contact_phone && <p>Telefon: {site.contact_phone}</p>}
          <WhatsAppButton area="general" text="Hallo! Ich habe eine Frage zu Kunst und Fund." testid="contact-whatsapp" fallback={<p className="text-[#6E6B65]" data-testid="whatsapp-missing">WhatsApp ist derzeit nicht verfügbar – nutze bitte das Formular.</p>} />
          <p className="pt-4"><Link className="underline" to="/bewerbung/kuenstler">Künstlerbewerbung</Link> · <Link className="underline" to="/bewerbung/lokal">Lokalbewerbung</Link></p>
        </div>
        <InquiryForm type="contact" />
      </div>
    </Page>
  );
}

export function Apply() {
  const { kind } = useParams();
  const artist = kind === "kuenstler";
  useSeo(artist ? "Künstlerbewerbung" : "Kunst für eure Räume");
  return (
    <Page className="max-w-3xl">
      <PageTitle eyebrow="Bewerbung" title={artist ? "Als Künstler:in ausstellen" : "Kunst für eure Räume"}>
        {artist ? "Erzähl uns von deiner Arbeit, füge Links oder Bilder hinzu. Konditionen werden individuell vereinbart." : "Für Lokale, Ordinationen, Kanzleien, Hotels, Büros, Studios und Musterwohnungen. Beschreibt euren Ort – wir melden uns."}
      </PageTitle>
      <section className="bg-[#F4EFEA] border border-[#E6DFD5] p-5 mb-8 space-y-4" data-testid="application-introduction">
        <h2 className="text-2xl font-black">{artist ? "Deine Kunst an echten Orten zeigen" : "Passende Kunst. Gemeinsam abgestimmt."}</h2>
        <p>{artist ? "Kunst & Fund verbindet Künstlerinnen und Künstler mit passenden Lokalen. Auf der Plattform können eigene Bilder und Fotokunst mit Künstlerprofil, Beschreibung und Kontaktmöglichkeit vorgestellt werden. Ob Originale oder Prints angeboten werden, stimmen wir gemeinsam ab." : "Kunst & Fund bringt Kunst und Fotokunst in Cafés, Restaurants und andere passende Orte. Neben dem Werk führt ein QR-Code zu Bild, Künstlerprofil und Angebot. Welche Arbeiten zu euch passen, besprechen wir persönlich."}</p>
        <p className="text-sm">{artist ? "So geht es los: Stell dich mit ein paar Beispielen vor. René meldet sich; Präsentation, Druckrechte, mögliche Ausstellungsorte und Verkaufskonditionen werden vor einer Zusammenarbeit abgestimmt." : "So geht es los: Erzählt uns kurz von euren Räumen und der verfügbaren Wandfläche. Danach klären wir gemeinsam Bildauswahl, Rahmen, Transport, Aufhängung, Übergabe und Zeitraum."}</p>
        <p className="text-sm">Wir starten mit einem kleinen Probebetrieb. Eine Bewerbung ist zunächst eine unverbindliche Anfrage und noch keine bestätigte Teilnahme oder Ausstellung.</p>
        <Link to="/kunst" className="inline-block underline font-semibold py-2">Aktuelle Kunst ansehen →</Link>
      </section>
      <InquiryForm type={artist ? "artist_application" : "venue_application"} />
    </Page>
  );
}

export function Legal() {
  const { slug } = useParams();
  const { data, loading } = useApi(`/public/pages/${slug}`);
  useSeo(data?.title);
  if (loading) return <Loading />;
  return (
    <Page className="max-w-3xl">
      <PageTitle title={data.title} />
      {data.unpublished ? <Empty testid="legal-unpublished">Dieser Text befindet sich in Bearbeitung und ist noch nicht freigegeben.</Empty> : <Prose text={data.body} />}
    </Page>
  );
}

export function QrResolve() {
  const { code } = useParams();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/public/qr/${code}`).then(({ data }) => {
      sessionStorage.setItem("kf_qr", code);
      if (data.artwork_slug) nav(`/kunst/${data.artwork_slug}?via=${code}`, { replace: true });
      else if (data.venue) nav(`/lokale/${data.venue.slug}?via=${code}`, { replace: true });
      else nav("/kunst", { replace: true });
    }).catch(() => setErr("Dieser QR-Code ist uns nicht bekannt."));
  }, [code, nav]);
  return err ? <Page><Empty testid="qr-unknown">{err} <Link to="/kunst" className="underline">Zum Gesamtangebot</Link></Empty></Page> : <Loading />;
}

export function NotFound() {
  useSeo("Seite nicht gefunden");
  return (
    <Page>
      <p className="font-display font-black text-[8rem] leading-none text-[#FF5A5F]">404</p>
      <h1 className="text-3xl font-black mt-4" data-testid="not-found">Diese Seite gibt es nicht.</h1>
      <p className="mt-4"><Link to="/" className="underline font-semibold">Zur Startseite</Link> · <Link to="/kunst" className="underline font-semibold">Kunst entdecken</Link></p>
    </Page>
  );
}


